import './DateAndTimeStep.scss';
import React, { useEffect, useState } from 'react';
import { Alert, Box, Fade, Grid, Grow, Tooltip, useTheme } from '@mui/material';
import moment, { Moment } from 'moment';
import 'moment-timezone';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { PickersDay, pickersDayClasses } from '@mui/x-date-pickers';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { isEmpty } from 'lodash';
import { usePostHog } from 'posthog-js/react';
import { useLanguage } from '../../../utils/GeneralUtils';

import 'animate.css';
import { quotePrice } from '../../core/pricing';
import PluginUtils from '../../PluginUtils';
import { OrderPageButton } from '../../themedComponents/OrderPageButton';
import { OrderPageTypography } from '../../themedComponents/OrderPageTypography';
import { OrderPageToggleButton } from '../../themedComponents/OrderPageToggleButton';
import RotatingLoader from '../../../ui/RotatingLoader';
import WeekendAndIsraeliHolidaysWarning from '../../components/WeekendAndIsraeliHolidaysWarning';
import { CustomDay } from './components/CustomDay';
import {
  AccountData,
  AccountLocationData,
  DateAvailabilityOut,
  OrderPageData,
  OrderPageTypes,
  TimeAvailabilityDisabledReason,
} from '../../../api';
import { isTimeSlotBookable } from './utils/orderCutoffUtils';
import { CondensedDatePicker } from './components/CondensedDatePicker';
import { CondensedOption, CondensedOptionsList } from './components/CondensedOptionsList';
import { hasAnyWidgetCustomTimeDetail, resolveWidgetCustomDetails } from './utils/customTimeDetails';
import { Troubleshoot } from './components/Troubleshoot';
import { Legend } from './components/Legend';
import { OrderPageDivider } from '../../themedComponents/OrderPageDivider';
import { useFilteredLocations } from '../../hooks/useFilteredLocations';
import { getLocationNameById } from '../../components/MultiLocationDetails';
import { OrderPageIntent } from '../../OrderPageIntent';
import CustomStaticDatePicker from './components/CustomStaticDatePicker';
import CourseSessionsList from './components/CourseSessionsList';
import { getDateFormat, getTimeFormat } from '../../../utils/getDateAndTimeFormat';
import TimeUtils from '../../../utils/TimeUtils';
import { safeStartSessionRecording } from '../../../utils/safePosthog';

export const OccupiedPickersDay = styled(PickersDay)(() => ({
  textDecoration: 'line-through',
}));
const BorderlessAlert = styled(Alert)({
  border: 'none',
  fontWeight: 'bold',
});
export const AvailablePickersDay = styled(PickersDay)(({ theme }) => ({
  background: theme.customTheme.palette.calendarPicker.availableDay.background,
  '&:hover': {
    background: theme.customTheme.palette.calendarPicker.availableDay.hover,
  },
  [`&&.${pickersDayClasses.selected}`]: {
    backgroundColor: theme.customTheme.palette.calendarPicker.availableDay.selected,
    border: `2px solid ${theme.customTheme.palette.border}`,
  },
}));

export const NotAvailablePickersDay = styled(PickersDay)(({ theme }) => ({
  color: 'black',
  textDecoration: 'line-through',
  background: theme.customTheme.palette.calendarPicker.fullyBookedDay.background,
  '&:hover': {
    background: theme.customTheme.palette.calendarPicker.fullyBookedDay.hover,
  },
  [`&&.${pickersDayClasses.selected}`]: {
    backgroundColor: theme.customTheme.palette.calendarPicker.fullyBookedDay.selected,
    border: `2px solid ${theme.customTheme.palette.border}`,
  },
}));

export const RunningOutPickersDay = styled(PickersDay)(({ theme }) => ({
  background: theme.customTheme.palette.calendarPicker.runningOutDay.background,
  '&:hover': {
    background: theme.customTheme.palette.calendarPicker.runningOutDay.hover,
  },
  [`&&.${pickersDayClasses.selected}`]: {
    backgroundColor: theme.customTheme.palette.calendarPicker.runningOutDay.selected,
    border: `2px solid ${theme.customTheme.palette.border}`,
  },
}));

export type OrderDetails = {
  persons: number;
  duration: number;
  price: number;
  rooms: number;
  custom_details?: Record<string, any>;
};
const DateAndTimeStep = ({
  shouldHideWeekends,
  orderPageIntent,
  orderPage,
  date,
  time,
  isLoadingDates,
  waitingListUrl,
  orderDetails,
  setDateTimeAndPrice,
  setTime,
  availableDates,
  specificEvent,
  setShouldDisableNext,
  troubleshoot,
  locations,
  initialAvailableDates,
  preSelectedDate,
  account,
}: {
  orderPage: OrderPageData;
  shouldHideWeekends?: boolean;
  orderPageIntent: OrderPageIntent;
  date?: string | null;
  time?: number | null;
  isLoadingDates: boolean;
  waitingListUrl?: string;
  orderDetails: OrderDetails;
  setDateTimeAndPrice: ({
    newDate,
    newTime,
    newPrice,
    newCourseId,
  }: {
    newDate?: string | null;
    newTime?: number | null;
    newPrice?: number;
    newCourseId?: string;
  }) => void;
  setTime: (newTime: number, courseId?: string) => void;
  availableDates: DateAvailabilityOut[];
  initialAvailableDates: DateAvailabilityOut[];
  troubleshoot?: boolean;
  specificEvent?: boolean;
  setShouldDisableNext?: (shouldDisableNext: boolean) => void;
  locations: AccountLocationData[];
  preSelectedDate?: string;
  account?: AccountData;
}) => {
  const posthog = usePostHog();
  const { longDateFormat, cardDateFormat } = getDateFormat(account);
  const theme = useTheme();
  // for some reason direction in theme is cool in iederpage but not here so hacking it manually
  const { i18n } = useTranslation();
  const dir = i18n.dir();
  const timeFormat = getTimeFormat(account);
  const initAvailableDatesDict = (availableDates: DateAvailabilityOut[]) => {
    const result = {};
    availableDates.forEach((dateObj) => {
      if (shouldHideWeekends && PluginUtils.isWeekendOrHoliday(dateObj.date, account?.holiday_calendar)) {
        return;
      }
      // @ts-ignore
      result[dateObj.date] = dateObj;
    });
    return result;
  };

  const availableDatesDict = initAvailableDatesDict(availableDates || []);

  const nonDisabledDays = availableDates?.filter((x) => !x.isDisabled);

  const isSingleOptionCourse =
    orderPage.order_page_type === OrderPageTypes.COURSE &&
    nonDisabledDays?.length === 1 &&
    nonDisabledDays[0].times.length === 1;

  const minDate = moment(nonDisabledDays?.[0]?.date);
  const firstAvailableDate = nonDisabledDays?.find((x) => moment(x.date).isSameOrAfter(minDate));
  const defaultFullObjDate = date ? availableDates?.find((obj) => obj.date === date) : firstAvailableDate;
  const [localDate, setLocalDate] = useState<null | Moment>(date ? moment(date) : moment());
  const referenceDate = moment(firstAvailableDate?.date);
  const [troubleshootVisible, setTroubleshootVisible] = useState(false);

  const [dateFullObj, setDateFullObj] = useState<null | undefined | DateAvailabilityOut>(defaultFullObjDate);
  // when any slot carries a custom "english" detail we label the unlabelled slots as hebrew everywhere else.
  const hasEnglishCustomTimeDetail = hasAnyWidgetCustomTimeDetail(nonDisabledDays || []);
  // order page can have a custom_detail with name "english" to signal that sessions are language-specific.
  // when present, a single-time date shows a language alert instead of toggle buttons (which are hidden with
  // only one option).
  const isLanguageSpecificOrderPage = orderPage.custom_details?.some((d) => d.name.toLowerCase() === 'english');
  const { t } = useTranslation();
  const language = useLanguage();

  const currentDateStartOfMonth = moment().startOf('month');
  const maxDate = moment().startOf('day').add(orderPage.max_date_in_days, 'days');

  const [selectedDateHasAllTimesDisabled, setSelectedDateHasAllTimesDisabled] = useState(false);
  const [ticketsLeftForSelectedTime, setTicketsLeftForSelectedTime] = useState<number>(0);
  const shouldShowDuration = orderPage.show_duration;
  const { numberOfTotalLocations } = useFilteredLocations({
    initialAvailableDates,
    defaultLocationId: orderPage.location_id,
  });
  const shouldShowLocation = numberOfTotalLocations > 1;
  const [selectedLocationId, setSelectedLocationId] = useState<string | undefined>();
  const clearDateAndTime = () => {
    if (specificEvent) {
      return;
    }
    setDateTimeAndPrice({ newDate: null, newTime: null });

    setLocalDate(null);
    setDateFullObj(null);
    setTroubleshootVisible(false);
  };

  useEffect(() => {
    if (preSelectedDate) {
      return;
    }
    if (!availableDates?.find((obj) => obj.date === date)) {
      clearDateAndTime();
      // setLocalDate(moment(availableDates?.find((x) => !x.isDisabled)?.date));
      return;
    }
    const dateObj = availableDates.find((obj) => obj.date === date);
    const foundTime = dateObj?.times.find((t) => t.seconds === time);
    if (!foundTime || foundTime.isDisabled) {
      clearDateAndTime();
    }
  }, [isLoadingDates]);

  const shouldDisableDate = (value: Moment) => {
    const formattedDate = value.format('YYYY-MM-DD');
    return (
      // @ts-ignore
      !availableDatesDict[formattedDate] ||
      // @ts-ignore
      (availableDatesDict[formattedDate].isDisabled && (!orderPage.waiting_list || !orderPage.waiting_list.enabled))
    );
  };

  const onDateChange = (newValue: Moment | null, shouldBlockScrollingIntoView = false) => {
    safeStartSessionRecording(posthog);
    setLocalDate(newValue);
    setTroubleshootVisible(false);
    const formattedDate = newValue?.format('YYYY-MM-DD');
    const dateFullObjTemp: DateAvailabilityOut | undefined = availableDates?.find((obj) => obj.date === formattedDate);

    let time;
    let courseId: string | undefined;
    if (dateFullObjTemp?.times.length === 1) {
      time = dateFullObjTemp?.times[0].seconds;
      courseId = dateFullObjTemp?.times[0].courseId;
      setTicketsLeftForSelectedTime(dateFullObjTemp.times[0].ticketsLeft || 0);
      if (setShouldDisableNext) {
        setShouldDisableNext(dateFullObjTemp.times[0].isDisabled);
      }
      setSelectedLocationId(dateFullObjTemp.times[0].location_id);
    }
    setSelectedDateHasAllTimesDisabled(
      !dateFullObjTemp?.times.find(
        (x) => !x.isDisabled && isTimeSlotBookable(x.seconds, formattedDate || '', orderPage, account),
      ),
    );

    const tempDateDetails = {
      date: formattedDate,
      time,
    };

    const { total: price } = quotePrice({
      orderPage,
      details: orderDetails,
      dateDetails: tempDateDetails,
      account,
    });
    setDateTimeAndPrice({
      newDate: tempDateDetails.date,
      newTime: tempDateDetails.time,
      newPrice: price,
      newCourseId: courseId,
    });
    setDateFullObj(dateFullObjTemp);
    if (!shouldBlockScrollingIntoView) {
      setTimeout(() => {
        document.getElementById('times-container')?.scrollIntoView({
          behavior: 'auto',
          block: 'center',
          inline: 'center',
        });
      }, 200);
    }
  };

  // a condensed option card carries a date and a time, so one click has to do what picking a day and then
  // picking a time do separately. onDateChange only auto-picks a time when the date has exactly one.
  const handleOptionSelect = (option: CondensedOption) => {
    onDateChange(moment(option.date), true);
    setTime(option.timeSlot.seconds, option.timeSlot.courseId);
    setSelectedLocationId(option.timeSlot.location_id);
    setTicketsLeftForSelectedTime(option.timeSlot.ticketsLeft || 0);
    if (setShouldDisableNext) {
      setShouldDisableNext(option.timeSlot.isDisabled);
    }
    setTroubleshootVisible(!option.isBookable && !!troubleshoot);
  };

  useEffect(() => {
    if (preSelectedDate) {
      onDateChange(moment(preSelectedDate), true);
    }
  }, [preSelectedDate, isLoadingDates, availableDates]);

  useEffect(() => {
    if (isSingleOptionCourse && !date && nonDisabledDays?.length === 1) {
      onDateChange(moment(nonDisabledDays[0].date), true);
    }
  }, [isSingleOptionCourse, availableDates]);

  const currentMonth = moment().month();
  // Get the next month, considering December (11) wraps to January (0)
  const nextMonth = (currentMonth + 1) % 12;

  let currentMonthCount = 0;
  let nextMonthCount = 0;
  let currentMonthTicketsLeft = 0;

  availableDates.forEach((date) => {
    const dateMonth = moment(date.date).month();
    if (dateMonth === currentMonth && moment(date.date).isSameOrAfter(moment(availableDates[0]?.date))) {
      currentMonthCount++;
      date.times.forEach((time) => {
        currentMonthTicketsLeft += (time.ticketsLeft || 0) > 0 ? time.ticketsLeft || 0 : 0;
      });
    } else if (dateMonth === nextMonth) {
      nextMonthCount++;
    }
  });
  const shouldShowLastTicketsNotification = currentMonthTicketsLeft > 0 && currentMonthTicketsLeft <= 5;
  // Check if both months have 3 or less counts
  const shouldShowCondensePicker = currentMonthCount <= 3 && nextMonthCount <= 3;
  // one card per date+time only reads well while each date has a handful of times - beyond that the
  // card list gets taller than the chip grid it replaces, so fall back to the chips.
  const maxTimesPerDate = Math.max(0, ...(availableDates || []).map((d) => d.times.length));
  const shouldShowOptionCards = (shouldShowCondensePicker && maxTimesPerDate <= 4) || isSingleOptionCourse;
  const selectedTimeSlot = time ? dateFullObj?.times.find((t) => t.seconds === time) : null;
  const courseSessions = selectedTimeSlot?.courseSessions;
  const ChosenTimeComponent = time ? (
    <Fade in={!!time} timeout={1000}>
      <Grid container flexDirection="column" id="times-container">
        {courseSessions && courseSessions.length > 0 ? (
          <CourseSessionsList courseSessions={courseSessions} longDateFormat={longDateFormat} timeFormat={timeFormat} />
        ) : (
          <>
            <Grid container gap={1} mt={2}>
              <OrderPageTypography bold variant="h6">
                {t('order_page.time', { activityLabelSingular: account?.activity_label_singular?.[language] || '' })}
              </OrderPageTypography>
              <Grid dir="ltr">
                <OrderPageTypography variant="h6">
                  {`${moment.unix(time).tz('utc').format(timeFormat)} - ${moment
                    .unix(time)
                    .tz('utc')
                    .add(orderDetails.duration, 'seconds')
                    .format(timeFormat)}`}
                </OrderPageTypography>
              </Grid>
            </Grid>
            <Grid container gap={1}>
              <OrderPageTypography bold variant="h6">
                {t('order_page.date')}
              </OrderPageTypography>
              <Grid>
                <OrderPageTypography variant="h6">{moment(date).format(longDateFormat)}</OrderPageTypography>
              </Grid>
            </Grid>
          </>
        )}
        {shouldShowLocation && selectedLocationId && (
          <Grid container gap={1}>
            <OrderPageTypography bold variant="h6">
              {t('order_page.location')}
            </OrderPageTypography>
            <OrderPageTypography variant="h6">
              {getLocationNameById(selectedLocationId, locations, language)}
            </OrderPageTypography>
          </Grid>
        )}
        {shouldShowDuration && (
          <Grid container gap={1}>
            <OrderPageTypography bold variant="h6">
              {t('order_page.duration', { activityLabelSingular: account?.activity_label_singular?.[language] || '' })}
            </OrderPageTypography>
            <OrderPageTypography variant="h6">{TimeUtils.formatDuration(orderDetails.duration, t)}</OrderPageTypography>
          </Grid>
        )}
        <Grid my={1}>
          <OrderPageDivider />
        </Grid>
      </Grid>
    </Fade>
  ) : null;
  return (
    <div className="date-and-time-step">
      <div className="dates-container" style={{ marginTop: '20px' }}>
        <OrderPageTypography variant="h3" bold>
          {orderPage.order_page_type === OrderPageTypes.COURSE
            ? t('order_page.choose_course_start_date')
            : preSelectedDate
            ? t('order_page.confirm_date')
            : t('order_page.choose_date')}
        </OrderPageTypography>
        <WeekendAndIsraeliHolidaysWarning
          orderPage={orderPage}
          orderPageIntent={orderPageIntent}
          shouldHideWeekends={shouldHideWeekends}
        />
        {isEmpty(availableDates) && isLoadingDates ? (
          <Grid container justifyContent="space-around" mt={3}>
            <Grid
              container
              minHeight={320}
              sx={{
                backgroundColor: theme.customTheme.palette.background,
                borderRadius: '12px',
                width: '322px',
                border: `1px solid ${theme.customTheme.palette.border}`,
              }}>
              <RotatingLoader />
            </Grid>
          </Grid>
        ) : isEmpty(availableDates) && !isLoadingDates ? (
          <Grid container justifyContent="space-around" mt={3}>
            <Grid
              container
              minHeight={336}
              justifyContent={'center'}
              alignItems={'center'}
              sx={{
                backgroundColor: theme.customTheme.palette.background,
                borderRadius: '12px',
                width: '322px',
                border: `1px solid ${theme.customTheme.palette.border}`,
              }}>
              <OrderPageTypography color="button.outlined" variant="h3">
                {t('order_page.noDates')}
              </OrderPageTypography>
            </Grid>
          </Grid>
        ) : shouldShowOptionCards ? (
          <>
            {orderPage.times_warning && (
              <BorderlessAlert severity="warning" variant="outlined">
                <OrderPageTypography color="warning">{orderPage.times_warning[language]}</OrderPageTypography>
              </BorderlessAlert>
            )}
            <CondensedOptionsList
              availableDates={availableDates}
              availableDatesDict={availableDatesDict}
              orderPage={orderPage}
              account={account}
              selectedDate={date}
              selectedTime={time}
              onSelectOption={handleOptionSelect}
              locations={locations}
              shouldShowLocation={shouldShowLocation}
              duration={orderDetails.duration}
              cardDateFormat={cardDateFormat}
              timeFormat={timeFormat}
              troubleshoot={troubleshoot}
              defaultExpanded={isSingleOptionCourse}
            />
            {troubleshootVisible && (
              <Troubleshoot timeSlotResponseOut={dateFullObj?.times.find((x) => x.seconds === time)} />
            )}
            {shouldShowDuration && time && (
              <Grid container gap={1} mt={1}>
                <OrderPageTypography bold variant="h6">
                  {t('order_page.duration', {
                    activityLabelSingular: account?.activity_label_singular?.[language] || '',
                  })}
                </OrderPageTypography>
                <OrderPageTypography variant="h6">
                  {TimeUtils.formatDuration(orderDetails.duration, t)}
                </OrderPageTypography>
              </Grid>
            )}
          </>
        ) : shouldShowCondensePicker ? (
          <CondensedDatePicker
            localDate={localDate}
            availableDatesDict={availableDatesDict}
            onDateChange={onDateChange}
            orderPage={orderPage}
            account={account}
          />
        ) : (
          <Grid container justifyContent="center">
            {shouldShowLastTicketsNotification && (
              <OrderPageTypography color="button.outlined" variant="h3">
                {t('order_page.lastTickets', {
                  ticketsAmount: currentMonthTicketsLeft,
                  month: moment().format('MMMM'),
                })}
              </OrderPageTypography>
            )}

            <CustomStaticDatePicker
              referenceDate={referenceDate}
              displayStaticWrapperAs="desktop"
              openTo="day"
              disablePast
              value={localDate}
              minDate={currentDateStartOfMonth}
              maxDate={maxDate}
              slots={{
                // @ts-ignore
                day: CustomDay,
              }}
              slotProps={{
                day: {
                  // @ts-ignore
                  date: localDate,
                  maxDate,
                  availableDatesDict,
                  troubleshoot,
                  orderPage,
                  account,
                },
              }}
              onChange={onDateChange}
              shouldDisableDate={shouldDisableDate}
              theme={theme}
              dir={dir}
            />
          </Grid>
        )}
        <Grid>{!shouldShowOptionCards && dateFullObj?.times.length === 1 && ChosenTimeComponent}</Grid>
        {!shouldShowOptionCards && dateFullObj?.times.length === 1 && isLanguageSpecificOrderPage && (
          <Grid mt={1}>
            <BorderlessAlert
              severity="info"
              sx={{
                backgroundColor: `${theme.customTheme.palette.primary}20`,
                color: theme.customTheme.palette.primary,
                border: `2px solid ${theme.customTheme.palette.primary}`,
                fontWeight: 'bold',
                '& .MuiAlert-icon': { color: theme.customTheme.palette.primary },
              }}>
              <OrderPageTypography bold>
                {t('order_page.workshop_held_in_language', {
                  activityLabelSingular: account?.activity_label_singular?.[language] || '',
                  language:
                    dateFullObj.times[0].custom_details &&
                    dateFullObj.times[0].custom_details.length > 0 &&
                    dateFullObj.times[0].custom_details[0].show_in_widget
                      ? dateFullObj.times[0].custom_details[0].label?.[language]
                      : language === 'he'
                      ? 'עברית'
                      : 'Hebrew',
                })}
              </OrderPageTypography>
            </BorderlessAlert>
          </Grid>
        )}
        {!shouldShowOptionCards && <Legend orderPage={orderPage} />}
      </div>

      {!shouldShowOptionCards && dateFullObj && dateFullObj.times.length !== 1 && (
        <Grow in timeout={1500}>
          <Grid container flexDirection="column" className="times-container" id="times-container" mt={1}>
            {dateFullObj.times.length !== 1 && (
              <Grid container flexDirection="column">
                <Grid item>
                  <OrderPageTypography variant="h3" bold>
                    {orderPage.times_title?.[language]
                      ? orderPage.times_title?.[language]
                      : t('order_page.choose_time')}
                  </OrderPageTypography>
                </Grid>
                {orderPage.times_warning && (
                  <BorderlessAlert severity="warning" variant="outlined">
                    <OrderPageTypography color="warning">{orderPage.times_warning[language]}</OrderPageTypography>
                  </BorderlessAlert>
                )}
                <Grid container justifyContent="center" className="times-buttons-container">
                  {dateFullObj.times
                    .sort((a, b) => a.seconds - b.seconds)
                    .map((availableTime, index) => {
                      const selected = time === availableTime.seconds;
                      const isCutoffDisabled = !isTimeSlotBookable(
                        availableTime.seconds,
                        dateFullObj.date,
                        orderPage,
                        account,
                      );
                      const availabletimeIsDisabled = availableTime.isDisabled || isCutoffDisabled;
                      return (
                        <Tooltip
                          title={
                            availabletimeIsDisabled
                              ? availableTime.timeDisableReason ===
                                TimeAvailabilityDisabledReason.VOUCHER_PERCENTAGE_LIMIT
                                ? t('troubleshoot.voucher_percentage_limit')
                                : t('order_page.time_not_available')
                              : ''
                          }
                          placement="top"
                          arrow>
                          <OrderPageToggleButton
                            customDetails={resolveWidgetCustomDetails(availableTime, hasEnglishCustomTimeDetail)}
                            key={`time-${index}`}
                            className={`time-button${availabletimeIsDisabled ? ' disabled-time-button' : ''}`}
                            troubleshoot={availabletimeIsDisabled && troubleshoot}
                            value={availableTime}
                            selected={selected}
                            disabled={availabletimeIsDisabled && !troubleshoot}
                            onChange={() => {
                              // for troubleshooting time
                              if (availabletimeIsDisabled) {
                                setTime(availableTime.seconds, availableTime.courseId);
                                setTroubleshootVisible(true);
                                return;
                              }
                              setTroubleshootVisible(false);
                              setTime(availableTime.seconds, availableTime.courseId);
                              setSelectedLocationId(availableTime.location_id);
                            }}>
                            {moment.unix(availableTime.seconds).tz('utc').format(timeFormat)}
                          </OrderPageToggleButton>
                        </Tooltip>
                      );
                    })}
                </Grid>
                {troubleshootVisible && (
                  <Troubleshoot timeSlotResponseOut={dateFullObj.times.find((x) => x.seconds === time)} />
                )}
              </Grid>
            )}

            {dateFullObj?.times.length !== 1 && <Grid mt={1}>{ChosenTimeComponent}</Grid>}
          </Grid>
        </Grow>
      )}
      {dateFullObj && selectedDateHasAllTimesDisabled && orderPage.waiting_list && orderPage.waiting_list.enabled && (
        <Box className="waiting-list-block">
          {ticketsLeftForSelectedTime <= 0 || (ticketsLeftForSelectedTime && ticketsLeftForSelectedTime > 0) ? (
            <Box className="warning">
              <Typography variant="h5">{t('order_page.waiting_list.no_tickets')}</Typography>
            </Box>
          ) : null}
          {waitingListUrl && (
            <Link to={`${waitingListUrl}/${dateFullObj.date}`}>
              <OrderPageButton variant="contained">
                <OrderPageTypography variant="h6" color="button.contained">
                  {t('order_page.waiting_list.link')}
                </OrderPageTypography>
              </OrderPageButton>
            </Link>
          )}
        </Box>
      )}
    </div>
  );
};

export default DateAndTimeStep;
